import type { CSSProperties } from 'react';

export const backgroundColor: string = '#eee2';
export const widgetBorderColor: string = '#ccc';
export const deviceColor: string = '#888';
export const alarmColor: string = '#E00';
export const activeColor: string = '#1a9'; // Green for deviceActive=1
export const traceColor: string = '#E80';
export const checkColor: string = '#4b4';
export const selectColor: string = '#f77';
export const checkpointColor: string = '#181';

// Device category colors
export const beaconColor: string = '#4A90E2'; // Blue for beacons (infrastructure)
export const checkinColor: string = '#50C878'; // Green for check-in stations
export const gatewayColor: string = '#9B59B6'; // Purple for gateways
export const lfDeviceColor: string = '#888'; // Gray for regular LF devices (same as deviceColor)
export const fontSize: string = 'clamp(16px, 3vmin, 32px)';
export const smallFontSize: string = 'clamp(13px, 2vmin, 22px)';
export const widgetRadius: string = '.6vmin';
export const buttonMinWidth: string = 'auto';
export const smallTextStyle: CSSProperties = { fontSize: 'max(13px, 2vmin)', color: '#555' };
export const widgetStyle: CSSProperties = {
  background: backgroundColor,
  fontSize: fontSize,
  margin: '1vmin',
  borderColor: widgetBorderColor,
};
export const buttonStyle: CSSProperties = {
  ...widgetStyle,
  minWidth: buttonMinWidth,
  padding: '1vmin',
  borderRadius: widgetRadius,
};
export const smallButtonStyle: CSSProperties = {
  ...buttonStyle,
  fontSize: 'max(13px, 1.7vmin)',
  padding: '0.5vmin',
  margin: '0.5vmin',
  background: '#fff8',
};
export const smallButtonStyle2: CSSProperties = {
  ...smallButtonStyle,
  fontSize: 'max(12px, 1.5vmin)',
  background: traceColor,
};
export const fetchStyle: CSSProperties = {
  position: 'fixed',
  right: '2vw',
  bottom: '2vh',
  fontSize: 'max(11px, 1.3vmin)',
};
export const filterStyle: CSSProperties = { ...widgetStyle, borderRadius: widgetRadius };

// Modal styles for Maintenance views
export const modalOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 3000,
};

export const modalContentStyle: CSSProperties = {
  background: 'white',
  borderRadius: '1vmin',
  padding: '2vmin',
  maxWidth: '500px',
  width: '90%',
  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
};

/**
 * Style for a Maintenance modal's scrollable body div.
 *
 * In a standalone (popout) modal the body owns its own scroll viewport. In `inline`
 * mode the modal is rendered inside the Maintenance panel, which provides the scroll —
 * so the body must NOT create a nested scroll container (doing so collapses to ~0 height
 * inside the panel's overflow:hidden wrapper). Returns an empty object when inline.
 */
export const scrollableModalBodyStyle = (inline?: boolean): CSSProperties =>
  inline
    ? {}
    : { overflowY: 'scroll', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', flex: 1 };

export const modalHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2vmin',
  borderBottom: '2px solid #ddd',
  paddingBottom: '1vmin',
};

export const modalCloseButtonStyle: CSSProperties = {
  background: '#f44',
  color: 'white',
  fontSize: '2vmin',
  padding: '0.5vmin 1vmin',
  border: 'none',
  borderRadius: '0.5vmin',
  cursor: 'pointer',
};

// Warning display styles
export const warningContainerStyle: CSSProperties = {
  marginTop: '2vmin',
  padding: '1.5vmin',
  background: '#fff3e0',
  borderRadius: '0.5vmin',
  border: '2px solid #ff9800',
};

/**
 * Get warning item style based on severity
 * @param severity - 'critical' or 'warning' or other string
 * @returns CSSProperties for the warning item
 */
export const getWarningItemStyle = (severity: string): CSSProperties => ({
  fontSize: 'max(12px, 1.5vmin)',
  padding: 'max(8px, 0.8vmin)',
  marginBottom: '0.5vmin',
  background: severity === 'critical' ? '#ffebee' : '#fff',
  borderLeft: `3px solid ${severity === 'critical' ? '#f44' : '#ff9800'}`,
  borderRadius: '0.3vmin',
});
